import { AI_ASSISTANT_TYPES, MessageSender, MessageType } from "./chat.constants";
import { AIConversationResponse, AIMessageResponse, TurnSegment } from "./chat.interface";

/**
 * Represents a message exchanged in a chat between a user and an AI.
 */
export class AIMessage {
  sent_by: MessageSender;
  sent_on: number;
  message_content: string;
  message_type: MessageType;
  message_id: string;
  thread_id: string;
  assistant_id?: string;
  assistant_type: string;
  is_complete: boolean;
  turnSegments: TurnSegment[] = [];
  streamError?: string;

  /**
   * Creates a new instance of the AIMessage class.
   * @param msg_response The AIMessageResponse object to create the message from.
   */
  constructor(msg_response: AIMessageResponse, isComplete: boolean = true) {
    // message metadata
    this.sent_by = msg_response.role;
    this.sent_on = msg_response.created_at * 1000;
    this.message_id = msg_response.id;
    this.thread_id = msg_response.thread_id;
    this.assistant_id = msg_response.assistant_id;

    // message content
    const content = msg_response.content.find(c => c.type === MessageType.Text)
    this.message_content = content?.text?.value || '';
    this.message_type = content?.type || MessageType.Text;

    // assistant type
    const model = AI_ASSISTANT_TYPES.find(m => m.value == msg_response.metadata?.aiAssistantType);
    this.assistant_type = model ? model.label : 'uTrade AI';

    // completion status
    this.is_complete = !!this.message_content && isComplete;
    this.turnSegments = [];
  }
}

/**
 * Represents a conversation with AI.
 */
export class AIConversation {
  thread_title: string;
  messages: AIMessage[];
  thread_id: string;
  first_id: string;
  last_id: string;
  has_more: boolean;
  is_running: boolean;
  is_new_thread: boolean;

  /**
   * Creates a new instance of AIConversation.
   * @param conv_response The AIConversationResponse object to create the conversation from.
   */
  constructor(conv_response: AIConversationResponse) {
    this.thread_title = conv_response.title || 'New Conversation';
    this.messages = conv_response.messages.data.sort((a, b) => a.created_at - b.created_at).map(message => new AIMessage(message));
    this.thread_id = conv_response.threadID;
    this.first_id = conv_response.messages.first_id;
    this.last_id = conv_response.messages.last_id;
    this.has_more = conv_response.messages.has_more;
    this.is_new_thread = conv_response.newThreadCreated || false;
    this.is_running = conv_response.threadRunning || false;
  }

  /**
   * Adds a message to the chat.
   * 
   * @param messageResponse - The response message to add.
   * @param isComplete - Indicates whether the message is complete. Default is true
   */
  addMessage(messageResponse: AIMessageResponse, isComplete: boolean = true): void {
    const message = new AIMessage(messageResponse, isComplete);
    this.messages.push(message);
    this.last_id = message.message_id;
  }

  /**
   * Gets the last message in the conversation.
   * @returns The last message in the conversation, or null if there are no messages.
   */
  getLastMessage(): AIMessage | null {
    return this.messages.length > 0 ? this.messages[this.messages.length - 1] : null;
  }

  /**
   * Gets all the messages in the conversation.
   * @returns An array of all the messages in the conversation.
   */
  getMessages(): AIMessage[] {
    return this.messages;
  }

  /**
   * Checks if there are any messages in the chat.
   * @returns {boolean} True if there are messages, false otherwise.
   */
  hasMessages(): boolean {
    return this.messages.length > 0;
  }
}

